import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getRecruits, type Recruit } from '../api/recruits';

const ACTIVE_STAGES = new Set(['application', 'screening', 'interview', 'offer']);
const ONBOARDING_STAGES = new Set([
  'contract',
  'onboarding',
  'training',
  'licensing',
  'live',
]);

type Stats = {
  activePipeline: number;
  newThisWeek: number;
  awaitingAction: number;
  onboarding: number;
};

function computeStats(recruits: Recruit[]): Stats {
  const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
  let activePipeline = 0;
  let newThisWeek = 0;
  let awaitingAction = 0;
  let onboarding = 0;
  for (const r of recruits) {
    if (ACTIVE_STAGES.has(r.stage)) activePipeline += 1;
    if (ONBOARDING_STAGES.has(r.stage)) onboarding += 1;
    if (r.stage === 'application') awaitingAction += 1;
    const created = new Date(r.createdAt).getTime();
    if (!Number.isNaN(created) && created >= weekAgo) newThisWeek += 1;
  }
  return { activePipeline, newThisWeek, awaitingAction, onboarding };
}

function StatCard({
  label,
  value,
  to,
  loading,
  testId,
}: {
  label: string;
  value: number;
  to: string;
  loading: boolean;
  testId: string;
}) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block bg-dg-panel/70 border border-dg-border rounded-lg p-5 hover:border-dg-red/60 hover:bg-dg-panel transition group"
    >
      <div className="text-[11px] uppercase tracking-[0.18em] text-dg-mist mb-2">
        {label}
      </div>
      {loading ? (
        <div
          data-testid={`${testId}-shimmer`}
          className="h-8 w-16 bg-white/5 rounded animate-pulse"
        />
      ) : (
        <div className="h-display text-4xl text-white group-hover:text-dg-red transition">
          {value}
        </div>
      )}
    </Link>
  );
}

export default function AppHome() {
  const user = useAuth((s) => s.user);
  const [recruits, setRecruits] = useState<Recruit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { recruits } = await getRecruits({ limit: '200' });
        if (!cancelled) setRecruits(recruits);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(
    () => (recruits ? computeStats(recruits) : null),
    [recruits]
  );
  const loading = recruits === null && !error;
  const empty = recruits !== null && recruits.length === 0;

  if (!user) return null;

  return (
    <main className="text-white">
      <section className="max-w-6xl mx-auto px-8 py-10">
        <h1 className="h-display text-4xl mb-2">
          Welcome, <span className="text-dg-red">{user.firstName}</span>
        </h1>
        <p className="text-dg-mist mb-8">
          Role: <code className="text-dg-text-2">{user.role}</code>
        </p>

        {error && (
          <div
            data-testid="dashboard-error"
            className="mb-6 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300"
          >
            Couldn't load dashboard data.
          </div>
        )}

        <div
          data-testid="dashboard-stats"
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <StatCard
            testId="stat-active-pipeline"
            label="Active Pipeline"
            value={stats?.activePipeline ?? 0}
            to="/recruiting/pipeline"
            loading={loading}
          />
          <StatCard
            testId="stat-new-this-week"
            label="New This Week"
            value={stats?.newThisWeek ?? 0}
            to="/recruiting/recruits"
            loading={loading}
          />
          <StatCard
            testId="stat-awaiting-action"
            label="Awaiting Action"
            value={stats?.awaitingAction ?? 0}
            to="/recruiting/pipeline"
            loading={loading}
          />
          <StatCard
            testId="stat-onboarding"
            label="Onboarding"
            value={stats?.onboarding ?? 0}
            to="/recruiting/recruits"
            loading={loading}
          />
        </div>

        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.18em] text-dg-mist mb-3">
            Quick actions
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/recruiting/applications/new"
              className="bg-dg-red hover:bg-dg-red-hover text-white text-sm font-medium px-4 py-2 rounded-md transition"
            >
              + New Application
            </Link>
            <Link
              to="/recruiting/pipeline"
              className="border border-dg-border-strong hover:border-dg-red text-sm font-medium px-4 py-2 rounded-md transition"
            >
              Open Pipeline
            </Link>
            <Link
              to="/recruiting/recruits"
              className="border border-dg-border-strong hover:border-dg-red text-sm font-medium px-4 py-2 rounded-md transition"
            >
              All Recruits
            </Link>
          </div>
        </div>

        {empty && !error && (
          <div
            data-testid="dashboard-empty"
            className="bg-dg-panel/60 border border-dg-border rounded-lg p-8 text-center mb-8"
          >
            <div className="h-display text-2xl mb-2">No recruits yet</div>
            <p className="text-dg-mist text-sm mb-4">
              Start your first application to fill the pipeline.
            </p>
            <Link
              to="/recruiting/applications/new"
              className="inline-block bg-dg-red hover:bg-dg-red-hover text-white text-sm font-medium px-4 py-2 rounded-md transition"
            >
              + New Application
            </Link>
          </div>
        )}

        <div className="bg-dg-panel/60 border border-dg-red/20 rounded-lg p-6">
          <h2 className="text-sm uppercase tracking-wider text-dg-mist mb-4">
            Tenant memberships
          </h2>
          {user.tenants && user.tenants.length > 0 ? (
            <ul className="space-y-2">
              {user.tenants.map((t) => (
                <li
                  key={t.tenantId}
                  className="flex items-center justify-between border-b border-dg-border last:border-0 pb-2"
                >
                  <div>
                    <div className="font-medium">{t.name || t.slug}</div>
                    <div className="text-xs text-dg-mist">
                      {t.slug} · status: {t.status}
                    </div>
                  </div>
                  <span className="text-xs uppercase tracking-wide text-dg-red">
                    {t.role}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-dg-mist text-sm">No tenant memberships.</p>
          )}
        </div>
      </section>
    </main>
  );
}
